import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import {
  BraintreeReduxActions,
  CheckoutAction,
  OrderModules,
  RootState,
  SentryLoggerInstance,
} from "../..";
import errorImage from "../../components/assets/images/illustration_error.png";
import ModalWithButton from "../../components/modals/ModalWithButton";
import { isUnexpectedError } from "../../hooks/useHandleOrderError";

export interface CheckoutErrorModalProps {
  onModalDismiss: () => void;
}

export const CHECKOUT_MODAL_ERROR_DISPLAYED = "CheckoutErrorModal was shown:";

export const CheckoutErrorModal = ({
  onModalDismiss,
}: CheckoutErrorModalProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { isTimeout: checkoutTimeout } = useSelector(
    (state: RootState) => state.braintree
  );
  const { error: checkoutError, hasUnexpectedError } = useSelector(
    (s: RootState) => s.checkout
  );
  const { getOrderResponse } = useSelector((s: RootState) => s.order);

  const isCheckoutModalVisible = useMemo(() => {
    return (
      (!!checkoutError || checkoutTimeout) &&
      !OrderModules.OrderUtils.isCheckoutInProgressError(checkoutError)
    );
  }, [checkoutError, checkoutTimeout]);

  useEffect(() => {
    if (checkoutError) {
      dispatch(CheckoutAction.resetPayment());
    }

    if (checkoutTimeout) {
      dispatch(BraintreeReduxActions.setTimeoutError(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = useCallback(() => {
    if (isUnexpectedError(hasUnexpectedError, checkoutError)) {
      return t("CheckoutPayment:unExpectedStatusTitle");
    } else if (checkoutTimeout) {
      return t("CheckoutPayment:timeoutTitle");
    } else {
      return checkoutError?.heading
        ? checkoutError.heading
        : t("CheckoutPayment:errorTitle");
    }
  }, [checkoutError, checkoutTimeout, hasUnexpectedError, t]);

  const message = useCallback(() => {
    if (isUnexpectedError(hasUnexpectedError, checkoutError)) {
      return t("CheckoutPayment:unExpectedStatusMessage");
    } else if (checkoutTimeout) {
      return t("CheckoutPayment:timeoutDesc");
    } else {
      return checkoutError?.message;
    }
  }, [checkoutError, checkoutTimeout, hasUnexpectedError, t]);

  /**
   * Sent checkout error to Sentry
   */
  useEffect(() => {
    if (isCheckoutModalVisible) {
      SentryLoggerInstance.sentryCaptureCustomError(
        CHECKOUT_MODAL_ERROR_DISPLAYED,
        `ORDER_ID: ${getOrderResponse?.orderId} ` +
          title() +
          ` ${message()}` +
          ` statusCode: ${checkoutError?.statusCode}`
      );
    }
  }, [
    checkoutError,
    checkoutTimeout,
    isCheckoutModalVisible,
    getOrderResponse,
    title,
    message,
  ]);

  return (
    <ModalWithButton
      isVisible={isCheckoutModalVisible}
      image={errorImage}
      small
      title={title()}
      messageBold={message()}
      primaryButton={{
        name: t("CheckoutPayment:tryAgainButton"),
        action: onModalDismiss,
      }}
      onModalClose={onModalDismiss}
    />
  );
};
